import type { CSSProperties, ReactNode } from 'react'
import Link from 'next/link'
import { AlertCircle, BadgeCheck, CheckCircle2, Clock3, Flag, ShieldCheck } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { PageHero } from '../ui/kit'

type PublicAction = {
  label: string
  href: string
  variant?: 'primary' | 'secondary' | 'ghost'
}

export function PublicPage({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`mx-public-page ${className}`}>{children}</div>
}

type HeroProps = {
  eyebrow: ReactNode
  title: string
  description: string
  actions?: PublicAction[]
  breadcrumbs?: ReactNode
  content?: ReactNode
  children?: ReactNode
  compact?: boolean
  image?: string
  imagePosition?: string
  className?: string
  ariaLabel?: string
}

/* DESIGN UNIFICATION Part 2a: every PublicDesign hero now delegates to the ONE
   PageHero (components/ui/kit) — same 1280px container, navy layered band, and
   typography on every standard page. The old rounded photo-card hero is gone. */
function HeroBase({
  eyebrow,
  title,
  description,
  actions,
  breadcrumbs,
  content,
  children,
  compact = false,
  className = '',
  ariaLabel,
}: HeroProps) {
  void className; void ariaLabel
  return (
    <PageHero
      compact={compact}
      motif="community"
      eyebrow={typeof eyebrow === 'string' ? eyebrow : 'Masjids.in'}
      title={<>{title}</>}
      subtitle={description}
      actions={(breadcrumbs || content || actions?.length) ? (
        <div className="mx-hero-extra">
          {breadcrumbs}
          {content}
          {actions?.length ? (
            <div className="mx-actions">
              {actions.map((action) => (
                <Link key={action.href + action.label} href={action.href} className={`mx-button ${action.variant || 'secondary'}`}>
                  {action.label}
                </Link>
              ))}
            </div>
          ) : null}
        </div>
      ) : undefined}
      widget={children}
    />
  )
}

export function HeroPrimary(props: Omit<HeroProps, 'compact'>) {
  return <HeroBase {...props} compact={false} className={`mx-hero-primary ${props.className || ''}`} />
}

export function HeroSecondary(props: Omit<HeroProps, 'compact'>) {
  return <HeroBase {...props} compact className={`mx-hero-secondary ${props.className || ''}`} />
}

export function PublicHero(props: HeroProps) {
  return <HeroBase {...props} />
}

export function PublicSection({
  eyebrow,
  title,
  description,
  action,
  children,
  className = '',
  id,
}: {
  eyebrow?: ReactNode
  title?: string
  description?: string
  action?: PublicAction
  children: ReactNode
  className?: string
  id?: string
}) {
  return (
    <section id={id} className={`mx-public-section mx-public-shell ${className}`}>
      {(eyebrow || title || description || action) && (
        <div className="mx-section-head">
          <div>
            {eyebrow ? <span className="mx-kicker">{eyebrow}</span> : null}
            {title ? <h2>{title}</h2> : null}
            {description ? <p>{description}</p> : null}
          </div>
          {action ? <Link href={action.href} className={`mx-button ${action.variant || 'ghost'}`}>{action.label}</Link> : null}
        </div>
      )}
      {children}
    </section>
  )
}

export function PublicCard({
  children,
  className = '',
  tone = 'light',
}: {
  children: ReactNode
  className?: string
  tone?: 'light' | 'glass' | 'dark'
}) {
  return <article className={`mx-card mx-card-${tone} ${className}`}>{children}</article>
}

export function ContentCard({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <article className={`mx-card mx-card-light mx-content-card ${className}`}>{children}</article>
}

export function IntelligenceCard({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <article className={`mx-card mx-card-dark mx-intelligence-card ${className}`}>{children}</article>
}

export function MetricCard({
  value,
  label,
  icon: Icon,
  note,
  className = '',
}: {
  value: string
  label: string
  icon?: LucideIcon
  note?: string
  className?: string
}) {
  return (
    <article className={`mx-metric-card ${className}`}>
      {Icon ? <span className="mx-metric-icon"><Icon size={17} /></span> : null}
      <strong>{value}</strong>
      <span>{label}</span>
      {note ? <small>{note}</small> : null}
    </article>
  )
}

type TrustRailItem = {
  label: string
  value: string
  tone?: 'verified' | 'pending' | 'neutral' | 'attention'
}

export function TrustRail({
  verified,
  claimed,
  updated,
  score,
  completeness,
  reportHref,
  items = [],
  compact = false,
}: {
  verified: string
  claimed: string
  updated: string
  score: string
  completeness: number
  reportHref: string
  items?: TrustRailItem[]
  compact?: boolean
}) {
  const defaultItems: TrustRailItem[] = [
    { label: 'Verified status', value: verified, tone: verified.toLowerCase().includes('verified') ? 'verified' : 'pending' },
    { label: 'Claimed status', value: claimed, tone: claimed.toLowerCase().includes('claimed') ? 'verified' : 'pending' },
    { label: 'Last updated', value: updated, tone: 'neutral' },
    { label: 'Community trust', value: score, tone: 'verified' },
  ]
  const railItems = items.length ? items : defaultItems

  return (
    <aside className={`mx-trust-rail ${compact ? 'is-compact' : ''}`} aria-label="Trust and verification">
      <div className="mx-trust-rail-head">
        <span><ShieldCheck size={15} /> Trust layer</span>
        <strong>{score}</strong>
      </div>
      <div className="mx-trust-items">
        {railItems.map((item) => {
          const Icon = item.tone === 'verified'
            ? BadgeCheck
            : item.tone === 'attention'
              ? AlertCircle
              : item.tone === 'pending'
                ? Clock3
                : CheckCircle2
          return (
            <div key={`${item.label}-${item.value}`} className={`mx-trust-item is-${item.tone || 'neutral'}`}>
              <Icon size={15} />
              <span>{item.label}</span>
              <strong>{item.value}</strong>
            </div>
          )
        })}
      </div>
      <div className="mx-trust-completeness">
        <span>Profile completeness</span>
        <strong>{Math.max(0, Math.min(100, Math.round(completeness)))}%</strong>
        <i><em style={{ width: `${Math.max(0, Math.min(100, completeness))}%` }} /></i>
      </div>
      <Link href={reportHref} className="mx-trust-report"><Flag size={14} /> Report an issue</Link>
    </aside>
  )
}

export function PublicGrid({ children, columns = 3, className = '' }: { children: ReactNode; columns?: 2 | 3 | 4; className?: string }) {
  return <div className={`mx-grid mx-grid-${columns} ${className}`}>{children}</div>
}

export function PublicBadge({ children, tone = 'emerald' }: { children: ReactNode; tone?: 'emerald' | 'gold' | 'navy' | 'muted' | 'danger' }) {
  return <span className={`mx-badge mx-badge-${tone}`}>{children}</span>
}

export function IconOrb({ icon: Icon, tone = 'emerald' }: { icon: LucideIcon; tone?: 'emerald' | 'gold' | 'navy' | 'danger' }) {
  return <span className={`mx-icon-orb mx-icon-${tone}`}><Icon size={18} /></span>
}

export function MiniStat({ value, label }: { value: string; label: string }) {
  return (
    <div className="mx-mini-stat">
      <strong>{value}</strong>
      <span>{label}</span>
    </div>
  )
}

export function PublicList({ items }: { items: string[] }) {
  return (
    <ul className="mx-check-list">
      {items.map((item) => <li key={item}>{item}</li>)}
    </ul>
  )
}

export function CTABand({
  eyebrow = 'Start with one action',
  title,
  actions,
}: {
  eyebrow?: string
  title: string
  actions: PublicAction[]
}) {
  return (
    <section className="mx-public-section mx-public-shell">
      <div className="mx-cta-band">
        <div>
          <span className="mx-kicker">{eyebrow}</span>
          <h2>{title}</h2>
        </div>
        <div className="mx-actions">
          {actions.map((action) => (
            <Link key={action.href + action.label} href={action.href} className={`mx-button ${action.variant || 'secondary'}`}>
              {action.label}
            </Link>
          ))}
        </div>
      </div>
    </section>
  )
}

export const PublicCTA = CTABand
